import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  NostroTransaction,
  TransactionStatusLog,
  Response,
  ChangeLog,
  Pagination,
  DocumentComment,
} from '@ui/src';
import { CustomerType } from '../../types';
import { customersComponentApi } from '../services/customers';

export interface ProblemNostroransactionState {
  transaction?: NostroTransaction;
  transactionId?: string;
  requestInProgress?: boolean;
  customerType?: CustomerType;
  logs?: TransactionStatusLog[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  transactionComments?: DocumentComment[];
  transactionCommentsPagination?: Pagination;
}

const initialState: ProblemNostroransactionState = {
  transaction: undefined,
  transactionId: undefined,
};

export const problemNostroTransactionSlice = createSlice({
  name: 'problemNostroTransaction',
  initialState,
  reducers: {
    clearTransactionState: (state) => {
      state.transaction = undefined;
      state.transactionId = undefined;
      state.customerType = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.transactionComments = undefined;
      state.transactionCommentsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getTransactionStatementsChangeLogs
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersTransactionLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<TransactionStatusLog>>) => {
        state.logs = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder
      .addMatcher(
        customersComponentApi.endpoints.getCustomerNostroTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: NostroTransaction }>) => {
          state.transaction = {
            ...action.payload.data,
          };
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getProblemTransactionComments
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<DocumentComment>> & {
            meta: {
              arg: {
                originalArgs: {
                  cursor?: string;
                };
              };
            };
          },
        ) => {
          let comments;

          if (!action.meta?.arg?.originalArgs?.cursor) {
            comments = action.payload.data.edges?.map((item) => item.node);
          } else {
            comments = ([] as DocumentComment[]).concat(
              state.transactionComments as DocumentComment[],
            );

            action.payload.data.edges?.forEach((item) =>
              comments.push(item.node),
            );
          }

          state.transactionComments = comments;
          state.transactionCommentsPagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.addCommentToProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          let comments: DocumentComment[] = [];

          if (state.transactionComments) {
            comments = ([] as DocumentComment[]).concat(
              state.transactionComments,
            );
          }

          comments.unshift(action.payload.data);

          state.transactionComments = comments;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.editCommentInProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          state.transactionComments = state.transactionComments?.map((item) => {
            const result = { ...item };

            if (result.id === item.id) {
              result.text = action.payload.data.text;
            }

            return result;
          });
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.deleteCommentInProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          const nextTranscationComments = state?.transactionComments?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          state.transactionComments = nextTranscationComments;
        },
      );
  },
});

export const { clearTransactionState } = problemNostroTransactionSlice.actions;

export default problemNostroTransactionSlice.reducer;
